import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const Terms: React.FC = () => {
  const sections = [
    {
      title: 'Accept af vilkår',
      content: `Ved at få adgang til og bruge DoMore-platformen accepterer du at være bundet af disse servicevilkår. Hvis du ikke accepterer disse vilkår, bedes du ikke bruge vores tjenester. Vi forbeholder os retten til at ændre disse vilkår til enhver tid, og din fortsatte brug af platformen udgør accept af eventuelle ændringer.`
    },
    {
      title: 'Brugerkonti',
      content: `Du skal oprette en konto for at bruge vores tjenester. Du er ansvarlig for at bevare fortroligheden af dine kontooplysninger og for alle aktiviteter under din konto. Du skal give nøjagtige og fuldstændige oplysninger, når du opretter din konto.`
    },
    {
      title: 'Værktøjsudlån og -lån',
      content: `Brugere kan liste værktøj til udlån eller låne værktøj gennem vores platform. Udlejere skal give nøjagtige beskrivelser og billeder af deres værktøj. Lejere skal bruge værktøj ansvarligt og returnere det i samme stand. Alle transaktioner skal gennemføres gennem vores platform.`
    },
    {
      title: 'Forsikring og ansvar',
      content: `Grundlæggende forsikringsdækning leveres for alle udlejninger. Dækningsgrænser og vilkår er specificeret i vores forsikringspolitik. Brugere er ansvarlige for eventuelle skader, der overstiger dækningsgrænserne. Vi er ikke ansvarlige for personskade eller ejendomsskade som følge af værktøjsbrug.`
    },
    {
      title: 'Forbudte aktiviteter',
      content: `Brugere må ikke: bruge platformen til ulovlige formål, chikanere andre brugere, give falske oplysninger, omgå vores gebyrsystem eller oprette flere konti. Overtrædelse kan resultere i kontolukning.`
    },
    {
      title: 'Intellektuel ejendomsret',
      content: `Alt platformindhold, herunder logoer, designs og software, er vores intellektuelle ejendom. Brugere bevarer ejerskabet af deres indhold, men giver os licens til at bruge det til platformformål.`
    },
    {
      title: 'Opsigelse',
      content: `Vi kan opsige eller suspendere konti for overtrædelser af disse vilkår. Brugere kan opsige deres konti til enhver tid. Ved opsigelse skal brugere fuldføre eventuelle igangværende transaktioner.`
    },
    {
      title: 'Tvistløsning',
      content: `Tvister mellem brugere bør først løses gennem vores løsningssystem. Uløste tvister kan være underlagt voldgift som specificeret i vores politik for tvistløsning.`
    },
    {
      title: 'Ansvarsbegrænsning',
      content: `Vi er ikke ansvarlige for indirekte, tilfældige eller følgeskader, der opstår som følge af din brug af platformen. Vores ansvar er begrænset til det beløb, der er betalt for den specifikke tjeneste, der er tale om.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Servicevilkår</h1>
          <p className="text-xl text-gray-600">
            Sidst opdateret: 11. februar 2026
          </p>
        </div>

        {/* Important Notice */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Læs venligst disse vilkår omhyggeligt, før du bruger DoMore. Ved at bruge vores tjenester 
                  accepterer du at være bundet af disse vilkår.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="max-w-3xl mx-auto">
          {sections.map((section, index) => (
            <div key={index} className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <div className="prose max-w-none">
                <p className="text-gray-600">{section.content}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="max-w-3xl mx-auto mt-16">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Spørgsmål om vores vilkår?</h2>
            <p className="text-gray-600 mb-6">
              Hvis du har spørgsmål om disse vilkår, bedes du kontakte vores supportteam.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="primary">Kontakt support</Button>
              </Link>
              <Link to="/help">
                <Button variant="outline">Besøg hjælpecenter</Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Related Links */}
        <div className="max-w-3xl mx-auto mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-600">
            <Link to="/privacy" className="hover:text-green-600">
              Privatlivspolitik
            </Link>
            <span className="hidden sm:inline text-gray-300">|</span>
            <Link to="/safety" className="hover:text-green-600">
              Sikkerhedsretningslinjer
            </Link>
            <span className="hidden sm:inline text-gray-300">|</span>
            <Link to="/help" className="hover:text-green-600">
              Hjælpecenter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;